import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { dossiersAPI, walletAPI } from '../services/api';

// ─── Helpers ────────────────────────────────────────────────────────────────

const STATUS_LABEL = {
  recu:         { label: 'Reçu',           color: '#1A55A0', bg: 'rgba(26,85,160,0.08)' },
  assigne:      { label: 'Assigné',         color: '#1A55A0', bg: 'rgba(26,85,160,0.08)' },
  en_cours:     { label: 'En cours',        color: '#1A55A0', bg: 'rgba(26,85,160,0.08)' },
  depose:       { label: 'Déposé',          color: '#1A55A0', bg: 'rgba(26,85,160,0.08)' },
  termine:      { label: 'Terminé',         color: '#1A7A4A', bg: 'rgba(26,122,74,0.08)' },
  rejete:       { label: 'Rejeté',          color: '#B03020', bg: 'rgba(176,48,32,0.08)' },
  doc_manquant: { label: 'Doc manquant',    color: '#d97706', bg: 'rgba(217,119,6,0.08)' },
};

const URGENCY_DOT = {
  rejete:       '#B03020',
  doc_manquant: '#B03020',
  recu:         '#d97706',
  assigne:      '#d97706',
  en_cours:     'rgba(26,25,22,0.2)',
  depose:       'rgba(26,25,22,0.2)',
  termine:      '#1A7A4A',
};

function Badge({ status }) {
  const s = STATUS_LABEL[status] || { label: status, color: '#888', bg: '#f0f0f0' };
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center',
      fontSize: '11px', fontWeight: 500,
      padding: '2px 8px', borderRadius: '4px',
      color: s.color, background: s.bg,
    }}>
      {s.label}
    </span>
  );
}

function UrgencyDot({ status, pulse }) {
  return (
    <div style={{
      width: '7px', height: '7px', borderRadius: '50%',
      background: URGENCY_DOT[status] || 'rgba(26,25,22,0.2)',
      flexShrink: 0,
      animation: pulse ? 'pulse 1s infinite' : 'none',
    }} />
  );
}

// ─── Styles constants ────────────────────────────────────────────────────────

const card = {
  background: '#fff',
  border: '0.5px solid rgba(0,0,0,0.07)',
  borderRadius: '12px',
  boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
};

const sectionTitle = {
  fontFamily: 'Syne, sans-serif',
  fontSize: '13px',
  fontWeight: 600,
  color: '#1A1916',
};

const rowHover = {
  display: 'flex', alignItems: 'center', gap: '10px',
  padding: '9px 16px',
  borderBottom: '0.5px solid rgba(0,0,0,0.05)',
  cursor: 'pointer',
  transition: 'background 0.12s',
};

const kpiMini = {
  background: '#F0EDE5',
  borderRadius: '7px',
  padding: '8px 10px',
  textAlign: 'center',
};

// ─── Component ───────────────────────────────────────────────────────────────

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [dossiers, setDossiers] = useState([]);
  const [wallet, setWallet]     = useState(null);
  const [loading, setLoading]   = useState(true);

  const plan        = user?.workspace?.plan || 'starter';
  const isPro       = plan === 'pro' || plan === 'enterprise';
  const prenom      = user?.prenom || '';

  // Greeting dynamique
  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Bonjour' : hour < 18 ? 'Bon après-midi' : 'Bonsoir';

  // Date lisible
  const dateStr = new Date().toLocaleDateString('fr-FR', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [dr, wr] = await Promise.all([
        dossiersAPI.list({ limit: 100 }),
        walletAPI.get(),
      ]);
      setDossiers(dr.data || []);
      setWallet(wr.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return (
    <div style={{ padding: '3rem', textAlign: 'center', color: 'rgba(26,25,22,0.4)', fontSize: '13px' }}>
      Chargement…
    </div>
  );

  // ── Computed stats ──────────────────────────────────────────────────────────
  // Filtre "ce mois-ci" basé sur la date du dossier (created_at)
  // → utilisé pour "Résumé mensuel" : reçus du mois, terminés du mois, taux du mois
  const now = new Date();
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const inThisMonth = (d) => {
    if (!d.created_at) return false;
    const dt = new Date(d.created_at);
    return dt >= monthStart;
  };
  const dossiersDuMois = dossiers.filter(inThisMonth);

  const total       = dossiers.length;               // total cumulé (tous mois confondus)
  const totalMois   = dossiersDuMois.length;         // reçus ce mois-ci
  const actifs      = dossiers.filter(d => ['recu','assigne','en_cours'].includes(d.status));
  const deposes     = dossiers.filter(d => d.status === 'depose');
  const bloques     = dossiers.filter(d => d.status === 'doc_manquant');
  const rejetes     = dossiers.filter(d => d.status === 'rejete');
  const termines    = dossiers.filter(d => d.status === 'termine');

  // Versions filtrées par mois (pour le bloc Résumé mensuel)
  const terminesMois = dossiersDuMois.filter(d => d.status === 'termine');
  const rejetesMois  = dossiersDuMois.filter(d => d.status === 'rejete');

  const tauxRejet   = total > 0 ? ((rejetes.length / total) * 100).toFixed(1) : '0.0';
  const tauxRejetMois = totalMois > 0 ? ((rejetesMois.length / totalMois) * 100).toFixed(1) : '0.0';
  // Alerte wallet uniquement si l'admin a explicitement configuré un seuil
  const walletBas   = wallet && wallet.alert_threshold && wallet.balance < wallet.alert_threshold;

  // Dossiers urgents : bloqués + rejetés en premier, puis actifs
  const urgents = [
    ...bloques.slice(0, 2),
    ...rejetes.slice(0, 2),
    ...actifs.slice(0, 3),
  ].slice(0, 5);

  // Alertes actives
  const alertes = [
    ...(bloques.length > 0 ? [{
      level: 'red', label: `${bloques.length} dossier${bloques.length > 1 ? 's' : ''} bloqué${bloques.length > 1 ? 's' : ''} — docs manquants`,
      path: '/dossiers?status=doc_manquant',
    }] : []),
    ...(rejetes.length > 0 ? [{
      level: 'red', label: `${rejetes.length} rejet${rejetes.length > 1 ? 's' : ''} INPI à corriger`,
      path: '/dossiers?status=rejete',
    }] : []),
    ...(walletBas ? [{
      level: 'amber', label: `Wallet sous le seuil d'alerte (${wallet.balance?.toFixed(0)} €)`,
      path: '/wallet',
    }] : []),
  ];

  const walletPct = wallet
    ? Math.min(100, Math.round((wallet.balance / (wallet.alert_threshold * 3.8)) * 100))
    : 0;

  return (
    <div>

      {/* ── Header ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.25rem' }}>
        <div>
          <div style={{ fontFamily: 'Syne, sans-serif', fontSize: '18px', fontWeight: 600, color: '#1A1916' }}>
            {greeting} {prenom} 👋
          </div>
          <div style={{ fontSize: '12px', color: 'rgba(26,25,22,0.4)', marginTop: '2px', textTransform: 'capitalize' }}>
            {dateStr}
          </div>
        </div>
        <Link to="/dossiers/nouveau" style={{ textDecoration: 'none' }}>
          <button style={{
            background: '#B8860B', color: '#fff', border: 'none',
            padding: '8px 16px', borderRadius: '7px', fontSize: '12px',
            fontWeight: 500, cursor: 'pointer', fontFamily: 'DM Sans, sans-serif',
          }}>
            + Nouveau dossier
          </button>
        </Link>
      </div>

      {/* ── Bandeau alertes ── */}
      {alertes.length > 0 && (
        <div style={{
          ...card,
          background: 'rgba(176,48,32,0.04)',
          border: '1px solid rgba(176,48,32,0.15)',
          padding: '10px 14px',
          marginBottom: '1.25rem',
          display: 'flex', alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap',
        }}>
          <div style={{ fontSize: '11px', fontWeight: 600, color: '#B03020', textTransform: 'uppercase', letterSpacing: '0.07em', paddingTop: '2px', flexShrink: 0 }}>
            ⚡ Actions requises
          </div>
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            {alertes.map((a, i) => (
              <div
                key={i}
                onClick={() => navigate(a.path)}
                style={{
                  background: '#fff',
                  border: `0.5px solid ${a.level === 'red' ? 'rgba(176,48,32,0.2)' : 'rgba(217,119,6,0.25)'}`,
                  borderRadius: '6px', padding: '3px 10px',
                  cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px',
                  fontSize: '12px', color: '#1A1916',
                  transition: 'border-color 0.12s',
                }}
                onMouseEnter={e => e.currentTarget.style.borderColor = a.level === 'red' ? '#B03020' : '#d97706'}
                onMouseLeave={e => e.currentTarget.style.borderColor = a.level === 'red' ? 'rgba(176,48,32,0.2)' : 'rgba(217,119,6,0.25)'}
              >
                <div style={{ width: '5px', height: '5px', borderRadius: '50%', background: a.level === 'red' ? '#B03020' : '#d97706' }} />
                <span dangerouslySetInnerHTML={{ __html: a.label.replace(/(\d+[^—]*)/, '<strong>$1</strong>') }} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── KPIs ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px', marginBottom: '1rem' }}>

        {/* Dossiers actifs */}
        <div
          onClick={() => navigate('/dossiers')}
          style={{ ...card, padding: '14px 16px', cursor: 'pointer' }}
        >
          <div style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.07em', color: 'rgba(26,25,22,0.28)', marginBottom: '5px' }}>Dossiers actifs</div>
          <div style={{ fontFamily: 'Syne, sans-serif', fontSize: '26px', fontWeight: 700, color: '#1A1916', lineHeight: 1 }}>{actifs.length}</div>
          <div style={{ display: 'flex', gap: '5px', marginTop: '7px', flexWrap: 'wrap' }}>
            {deposes.length > 0 && <span style={{ fontSize: '10px', background: 'rgba(26,85,160,0.08)', color: '#1A55A0', padding: '1px 6px', borderRadius: '3px' }}>{deposes.length} déposés</span>}
            {bloques.length > 0 && <span style={{ fontSize: '10px', background: 'rgba(176,48,32,0.08)', color: '#B03020', padding: '1px 6px', borderRadius: '3px' }}>{bloques.length} bloqués</span>}
          </div>
        </div>

        {/* Suivi INPI */}
        <div
          onClick={() => navigate('/dossiers/inpi')}
          style={{ ...card, padding: '14px 16px', cursor: 'pointer' }}
        >
          <div style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.07em', color: 'rgba(26,25,22,0.28)', marginBottom: '5px' }}>Suivi INPI</div>
          <div style={{ fontFamily: 'Syne, sans-serif', fontSize: '26px', fontWeight: 700, color: '#1A1916', lineHeight: 1 }}>{deposes.length}</div>
          <div style={{ display: 'flex', gap: '5px', marginTop: '7px', flexWrap: 'wrap' }}>
            {rejetes.length > 0 && <span style={{ fontSize: '10px', background: 'rgba(176,48,32,0.08)', color: '#B03020', padding: '1px 6px', borderRadius: '3px' }}>{rejetes.length} rejetés</span>}
            <span style={{ fontSize: '10px', background: 'rgba(26,122,74,0.08)', color: '#1A7A4A', padding: '1px 6px', borderRadius: '3px' }}>{termines.length} validés</span>
          </div>
        </div>

        {/* Wallet */}
        <div
          onClick={() => navigate('/wallet')}
          style={{
            ...card,
            padding: '14px 16px', cursor: 'pointer',
            ...(walletBas ? { background: 'rgba(184,134,11,0.03)', border: '0.5px solid rgba(184,134,11,0.2)' } : {}),
          }}
        >
          <div style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.07em', color: 'rgba(26,25,22,0.28)', marginBottom: '5px' }}>Wallet INPI</div>
          <div style={{
            fontFamily: 'Syne, sans-serif', fontSize: '22px', fontWeight: 700, lineHeight: 1,
            color: wallet?.balance > (wallet?.alert_threshold || 3000) ? '#1A7A4A' : '#d97706',
          }}>
            {wallet ? `${wallet.balance.toLocaleString('fr-FR', { maximumFractionDigits: 0 })} €` : '—'}
          </div>
          <div style={{ marginTop: '7px' }}>
            <div style={{ height: '3px', background: '#F0EDE5', borderRadius: '2px', overflow: 'hidden' }}>
              <div style={{ width: `${walletPct}%`, height: '100%', background: walletBas ? '#d97706' : '#1A7A4A', borderRadius: '2px', transition: 'width 0.3s' }} />
            </div>
            <div style={{ fontSize: '10px', color: 'rgba(26,25,22,0.35)', marginTop: '3px' }}>
              seuil {wallet?.alert_threshold?.toLocaleString('fr-FR') || '3 000'} €
            </div>
          </div>
        </div>

        {/* Ce mois — basé sur created_at (date du dossier) */}
        <div style={{ ...card, padding: '14px 16px' }}>
          <div style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.07em', color: 'rgba(26,25,22,0.28)', marginBottom: '5px' }}>Ce mois</div>
          <div style={{ fontFamily: 'Syne, sans-serif', fontSize: '26px', fontWeight: 700, color: '#1A1916', lineHeight: 1 }}>{totalMois}</div>
          <div style={{ display: 'flex', gap: '5px', marginTop: '7px' }}>
            <span style={{ fontSize: '10px', background: 'rgba(26,122,74,0.08)', color: '#1A7A4A', padding: '1px 6px', borderRadius: '3px' }}>{terminesMois.length} terminés</span>
            {Number(tauxRejetMois) > 0 && (
              <span style={{ fontSize: '10px', background: 'rgba(176,48,32,0.08)', color: '#B03020', padding: '1px 6px', borderRadius: '3px' }}>{tauxRejetMois}% rejet</span>
            )}
          </div>
        </div>

      </div>

      {/* ── Ligne principale ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>

        {/* Dossiers urgents */}
        <div style={card}>
          <div style={{ padding: '12px 16px', borderBottom: '0.5px solid rgba(0,0,0,0.06)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={sectionTitle}>À traiter en priorité</div>
            <Link to="/dossiers" style={{ fontSize: '11px', color: '#B8860B', textDecoration: 'none' }}>Tous les dossiers →</Link>
          </div>
          {urgents.length === 0 ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'rgba(26,25,22,0.35)', fontSize: '12px' }}>
              ✓ Aucun dossier urgent
            </div>
          ) : urgents.map(d => (
            <div
              key={d.id}
              onClick={() => navigate('/dossiers')}
              style={rowHover}
              onMouseEnter={e => e.currentTarget.style.background = '#F5F3EE'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              <UrgencyDot status={d.status} pulse={['rejete','doc_manquant'].includes(d.status)} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '12px', fontWeight: 500, color: '#1A1916', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {d.reference} · {d.societe_nom || '—'}
                </div>
                <div style={{ fontSize: '11px', color: 'rgba(26,25,22,0.4)', marginTop: '1px' }}>
                  {d.type?.replace(/_/g, ' ')}
                  {d.sla_deadline && (
                    <span style={{ color: '#d97706', marginLeft: '6px' }}>
                      · SLA {new Date(d.sla_deadline).toLocaleDateString('fr-FR')}
                    </span>
                  )}
                </div>
              </div>
              <Badge status={d.status} />
            </div>
          ))}
        </div>

        {/* Colonne droite */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>

          {/* Stats mois — basé sur created_at (date du dossier) */}
          <div style={{ ...card, padding: '14px 16px' }}>
            <div style={{ ...sectionTitle, marginBottom: '12px' }}>
              Résumé mensuel
              <span style={{ fontSize: '10px', color: 'rgba(26,25,22,0.35)', fontWeight: 400, marginLeft: '6px', textTransform: 'none', letterSpacing: 0 }}>
                · {now.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}
              </span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
              <div style={kpiMini}>
                <div style={{ fontFamily: 'Syne, sans-serif', fontSize: '18px', fontWeight: 700, color: '#1A1916' }}>{totalMois}</div>
                <div style={{ fontSize: '10px', color: 'rgba(26,25,22,0.4)', marginTop: '1px' }}>Dossiers reçus</div>
              </div>
              <div style={kpiMini}>
                <div style={{ fontFamily: 'Syne, sans-serif', fontSize: '18px', fontWeight: 700, color: '#1A7A4A' }}>{terminesMois.length}</div>
                <div style={{ fontSize: '10px', color: 'rgba(26,25,22,0.4)', marginTop: '1px' }}>Terminés</div>
              </div>
              <div style={kpiMini}>
                <div style={{ fontFamily: 'Syne, sans-serif', fontSize: '18px', fontWeight: 700, color: totalMois > 0 ? '#1A7A4A' : '#1A1916' }}>
                  {totalMois > 0 ? `${Math.round((terminesMois.length / totalMois) * 100)}%` : '—'}
                </div>
                <div style={{ fontSize: '10px', color: 'rgba(26,25,22,0.4)', marginTop: '1px' }}>Taux résolution</div>
              </div>
              <div style={kpiMini}>
                <div style={{ fontFamily: 'Syne, sans-serif', fontSize: '18px', fontWeight: 700, color: Number(tauxRejetMois) > 5 ? '#B03020' : '#1A1916' }}>
                  {tauxRejetMois}%
                </div>
                <div style={{ fontSize: '10px', color: 'rgba(26,25,22,0.4)', marginTop: '1px' }}>Taux rejet</div>
              </div>
            </div>
          </div>

          {/* Pilotage teaser ou lien */}
          {isPro ? (
            <Link to="/pilotage" style={{ textDecoration: 'none' }}>
              <div style={{ ...card, padding: '14px 16px', cursor: 'pointer' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <div style={sectionTitle}>Dashboard pilotage</div>
                  <span style={{ fontSize: '11px', color: '#B8860B' }}>Voir →</span>
                </div>
                <div style={{ fontSize: '12px', color: 'rgba(26,25,22,0.5)', lineHeight: 1.5 }}>
                  Conformité, délais, benchmarking et rapports automatiques.
                </div>
              </div>
            </Link>
          ) : (
            <div style={{ ...card, padding: '14px 16px', background: 'rgba(184,134,11,0.02)', border: '0.5px solid rgba(184,134,11,0.15)' }}>
              <div style={{ ...sectionTitle, marginBottom: '6px' }}>Débloquez le pilotage</div>
              <div style={{ fontSize: '11px', color: 'rgba(26,25,22,0.45)', lineHeight: 1.5, marginBottom: '10px' }}>
                Conformité, délais, benchmarking et rapports automatiques disponibles en Pro.
              </div>
              <Link to="/facturation">
                <button style={{ background: '#B8860B', color: '#fff', border: 'none', padding: '6px 14px', borderRadius: '6px', fontSize: '11px', fontWeight: 500, cursor: 'pointer' }}>
                  Passer à Pro →
                </button>
              </Link>
            </div>
          )}

        </div>
      </div>

      {/* ── Ligne basse : intégrations (à venir) ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' }}>
        {[
          { title: 'JAL', desc: 'Publication automatique des annonces légales et récupération des attestations.' },
          { title: 'Aircall', desc: 'Suivi des appels entrants, taux de décroché et durée moyenne par dossier.' },
          { title: 'Zendesk', desc: 'Tickets clients, délais de résolution et suivi des dossiers en attente.' },
        ].map(c => (
          <div key={c.title} style={{ ...card, padding: '14px 16px', position: 'relative', opacity: 0.92 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <div style={sectionTitle}>{c.title}</div>
              <span style={{ fontSize: '10px', background: 'rgba(184,134,11,0.08)', color: '#B8860B', padding: '1px 8px', borderRadius: '3px', fontWeight: 500 }}>
                Disponible prochainement
              </span>
            </div>
            <div style={{ fontSize: '12px', color: 'rgba(26,25,22,0.5)', lineHeight: 1.5 }}>
              {c.desc}
            </div>
          </div>
        ))}
      </div>

      <style>{`@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:.35} }`}</style>
    </div>
  );
}
